import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useTranslation } from "react-i18next";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  explain,
  explainCard,
  plot,
  understand,
  getMarkingUsageSummary,
  DEPTH_LABELS,
  DEPTH_COLORS,
  DIRECTIVE_PRESETS,
  type Depth,
  type LookupKind,
  type MarkingUserResponse,
  type UnderstandResponse,
} from "@/api/lecture";
import {
  loadPdf,
  loadMarks,
  saveMarks,
  loadUnderstanding,
  saveUnderstanding,
} from "@/create/lecture/lecture-pdf-store";
import { RichText, previewText } from "@/components/rich-text";
import { ApiError } from "@/api/client";
import { Alert, Button, Spinner } from "@/components/ui";
import { useQuery } from "@tanstack/react-query";
import { getManifest } from "@/api/ai-settings";

// Per-device sticky default for the lookup model dropdown. "" = Auto (the user's
// global marking setting / the default fast model). Value is "provider::model".
const MARK_MODEL_KEY = "eddi.markLookupModel";
function loadMarkModel(): string {
  try {
    return localStorage.getItem(MARK_MODEL_KEY) ?? "";
  } catch {
    return "";
  }
}

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

// The reusable core of the PDF-marking experience. It renders a PDF, lets the
// learner snip regions to Explain / Ask / Mark-to-study, and persists marks +
// highlights locally (IndexedDB) keyed by `pdfRef`. It is run-agnostic: the only
// outward contract is the optional `primaryAction`, whose `onClick` receives the
// exact `MarkingUserResponse` the lecture framework's marking step expects.
//
// Two callers wrap this:
//  • the lecture framework run renderer (Create → generate a set), and
//  • the standalone Study page (mark & study a PDF, no generation required).
// Both share this component so a study-mode "Generate" emits a byte-identical
// payload to a normal lecture run.
//
// Two ways to mark on web:
//  • Region-snip — drag a rectangle over the rendered page → snip that area to a
//    PNG → either ask the multimodal model about it (Explain/Ask, vision reads
//    the equation/figure) or mark it for study (a deep mark). Uses pdf.js's
//    stable canvas render. Best for figures/equations.
//  • Text selection — a pdf.js text layer overlays the canvas so dragging across
//    wrapping text makes a native, multi-line selection. We capture the real
//    selected string (→ grounds real definitions) plus its per-line rects (→ a
//    text-highlight shape, not one fat bounding box).
//
// A toolbar `mode` picks what a completed gesture does: "mark" opens the action
// popover (Explain / Ask / Mark-to-study), while "highlight" turns it straight
// into a plain visual HighlightLocal — no popover, no LLM. Highlights are
// excluded from the generation payload; a per-highlight "Mark to study" promotes
// one into a deep mark when the learner wants it folded into a generated set.

const FIT_PADDING = 24; // the scroll container's p-3 (12px × 2)
const DEFAULT_FIT = 700; // px, until the container is measured
const DPR_CAP = 2; // cap device-pixel-ratio for memory

type NormRect = { x: number; y: number; w: number; h: number }; // 0..1 of the page

interface DeepMarkLocal {
  id: string;
  label: string;
  depth: Depth;
  page: number;
  rect: NormRect; // bounding box (snip + move/resize anchor)
  rects?: NormRect[]; // per-line rects for a text-selection highlight (render-only)
  selectedText?: string; // real selected text (text marks); "" for region snips
  imageB64: string;
  color?: string;
  directives?: string[];
}

// One removed mark held for undo: the item plus its original array index, so a
// restore can splice it back into place (order-preserving).
type RemovedEntry =
  | { kind: "lookup"; item: LookupLocal; index: number }
  | { kind: "deep"; item: DeepMarkLocal; index: number }
  | { kind: "highlight"; item: HighlightLocal; index: number };

// ── Lookup failure helpers ──────────────────────────────────────────────────
// A transient failure (rate-limit / server blip / network / timeout) is worth an
// automatic retry before we bother the user; a 4xx (bad request, auth) is not.
function isTransientErr(err: unknown): boolean {
  if (err instanceof ApiError) return err.status === 429 || err.status >= 500;
  return true; // network / abort / unknown → treat as transient (retry once)
}
// The real, human-readable reason to show on the failed row.
function errText(err: unknown): string {
  if (err instanceof ApiError) return err.message || `Request failed (${err.status})`;
  if (err instanceof Error && err.message) return err.message;
  return "Lookup failed — tap Retry.";
}
// Run `fn`; on a transient failure, wait a beat and retry ONCE before giving up.
function callWithRetry<T>(fn: () => Promise<T>): Promise<T> {
  return fn().catch((err) => {
    if (!isTransientErr(err)) throw err;
    return new Promise<void>((r) => setTimeout(r, 800)).then(fn);
  });
}

interface LookupLocal {
  id: string;
  kind: LookupKind;
  question?: string;
  selectedText?: string;
  explanation: string;
  /** Async lifecycle of the background lookup. A missing value counts as
   *  "ready" so pre-status saves keep rendering exactly as before. */
  status?: "loading" | "ready" | "error";
  /** Human-readable failure reason surfaced on `status === "error"` (the real
   *  cause — rate-limited / timed out — instead of a generic message). */
  errorMsg?: string;
  /** Which backend call settles this lookup — so a retry re-fires the SAME kind
   *  of request. "explain" (the shallow /explain blurb, default), "plot" (the
   *  sandboxed plot agent), or "card" (the specialist deep card). Missing =
   *  "explain" for back-compat with pre-resolver saves. */
  resolver?: "explain" | "plot" | "card";
  // Specialist deep-card facets (the "Explain in depth" action). Optional so
  // shallow/legacy lookups still serialize.
  definition?: string;
  intuition?: string;
  answer?: string;
  page: number;
  rect: NormRect;
  rects?: NormRect[];
  imageB64: string;
  keep: boolean;
  color?: string;
  /** For a "plot" lookup: the URL of the chart the plot agent rendered (vs
   *  `imageB64`, which stays the source snip). Shown in the detail modal and
   *  forwarded into the generated set. */
  imageUrl?: string;
}

// A plain visual highlight — a highlighter pen. No depth, directives, or
// explanation: it carries zero AI semantics and is excluded from the generation
// payload (`buildMarkingPayload`). Reuses the shared rect/rects/imageB64 shape so
// it slots into the existing render + select + recolor + resize machinery. A
// highlight is "includable" only via an explicit promote-to-study action.
interface HighlightLocal {
  id: string;
  page: number;
  rect: NormRect;
  rects?: NormRect[]; // per-line rects for a text-selection highlight
  selectedText?: string;
  imageB64: string;
  color?: string;
}

// Recolor palette (deep marks default yellow, lookups default blue).
const MARK_COLORS = ["#eab308", "#3b82f6", "#ef4444", "#22c55e", "#a855f7", "#f97316"];

// Default highlighter colour (yellow, matching MARK_COLORS[0]).
const HIGHLIGHT_COLOR = MARK_COLORS[0];

interface Saved {
  deep: DeepMarkLocal[];
  lookups: LookupLocal[];
  highlights?: HighlightLocal[]; // optional for back-compat with pre-highlight saves
}

const DEPTHS: Depth[] = ["from_scratch", "recap_briefly", "already_know"];

// Per-depth Tailwind classes for the prominent Mark-to-study buttons, matching
// the DEPTH_COLORS heat scheme (amber / blue / green).
const DEPTH_BTN_CLASSES: Record<Depth, string> = {
  from_scratch: "bg-amber-100 text-amber-800 hover:bg-amber-200",
  recap_briefly: "bg-blue-100 text-blue-800 hover:bg-blue-200",
  already_know: "bg-green-100 text-green-800 hover:bg-green-200",
};

function uid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `m-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function stripB64(dataUrl: string): string {
  const i = dataUrl.indexOf(",");
  return i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
}

/** Render one page to a base64 PNG on an offscreen canvas — the input for the
 *  understanding image fallback on scanned/handwritten PDFs. Independent of the
 *  display-render effect (avoids a race on first load). Returns null on failure. */
async function renderPageToB64(
  doc: PDFDocumentProxy,
  pageNum: number,
  targetWidth = 1280,
): Promise<{ b64: string; mime: string } | null> {
  try {
    const page = await doc.getPage(pageNum);
    const vp1 = page.getViewport({ scale: 1 });
    const scale = Math.min(2.5, targetWidth / vp1.width);
    const vp = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(vp.width);
    canvas.height = Math.round(vp.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    return { b64: stripB64(canvas.toDataURL("image/png")), mime: "image/png" };
  } catch {
    return null;
  }
}

/** A readable mark label: the selected text (trimmed) for a text mark, else the
 *  page-relative "Region · p.N" fallback for a figure snip. */
function labelFor(p: { page: number; selectedText?: string }): string {
  const t = (p.selectedText ?? "").trim().replace(/\s+/g, " ");
  if (!t) return `Region · p.${p.page}`;
  return t.length > 48 ? `${t.slice(0, 47).trimEnd()}…` : t;
}

// A pending snip/selection awaiting an action.
interface Pending {
  page: number;
  rect: NormRect;
  rects?: NormRect[]; // present for a text selection (per-line)
  selectedText?: string; // present for a text selection
  imageB64: string;
  anchorTop: number; // page-relative px for the popover
  anchorLeft: number;
  /** The id of the plain highlight this gesture created (both modes), so the
   *  unified popover's colour / study-level / look-up buttons act on that
   *  existing mark instead of spawning a duplicate. */
  markId?: string;
}

/** Compact bullet string from a one-time document understanding — passed as
 *  `context` on lookups + as `document_context` in the marking payload. */
function buildContextText(u: UnderstandResponse): string {
  const parts: string[] = [];
  if (u.summary?.trim()) parts.push(u.summary.trim());
  const learnings = (u.learnings ?? []).filter((l) => l.trim());
  if (learnings.length) parts.push(learnings.map((l) => `• ${l}`).join("\n"));
  return parts.join("\n\n").slice(0, 4000);
}

/** Build the lecture marking user_response from local marks (the forward-compat
 *  contract: identical for run-mode and study-mode). Exported so callers can
 *  serialize the persisted marks without re-mounting the workspace. */
export function buildMarkingPayload(
  deep: DeepMarkLocal[],
  lookups: LookupLocal[],
  documentContext = "",
  highlights: HighlightLocal[] = [],
): MarkingUserResponse {
  return {
    deep_marks: deep.map((d) => ({
      id: d.id,
      // Real selected text (text marks) grounds the topic's card + questions;
      // region snips stay image-only ("").
      selected_text: d.selectedText ?? "",
      label: d.label,
      depth: d.depth,
      image_b64: d.imageB64,
      image_mime: "image/png",
      color: d.color,
      directives: d.directives,
      page: d.page - 1,
    })),
    lookups: lookups
      // Exclude still-loading / failed background lookups (a missing status is a
      // legacy/ready lookup and passes).
      .filter((l) => l.keep && l.explanation && l.status !== "loading" && l.status !== "error")
      .map((l) => ({
        id: l.id,
        kind: l.kind,
        // Prefer the actual selection; fall back to the question for a legacy
        // selection-less "ask". (A deep card always has a selection.)
        selected_text: l.selectedText || l.question || "",
        explanation: l.explanation,
        // Specialist deep-card facets — folded structurally by the backend.
        definition: l.definition,
        intuition: l.intuition,
        question: l.question,
        answer: l.answer,
        // A plot lookup carries the rendered chart URL; the backend prefers
        // image_url over the snip when folding it into the reference item.
        image_url: l.imageUrl,
        image_b64: l.imageB64,
        image_mime: "image/png",
        color: l.color,
        page: l.page - 1,
      })),
    // Plain highlights → a soft, text-only emphasis signal. Region-only
    // highlights (no selected text) carry nothing to steer text generation, so
    // they're dropped here; the payload stays cheap (no snip bytes).
    highlights: highlights
      .filter((h) => (h.selectedText ?? "").trim().length > 0)
      .map((h) => ({
        id: h.id,
        selected_text: (h.selectedText ?? "").trim(),
        page: h.page - 1,
        color: h.color,
      })),
    document_context: documentContext || undefined,
  };
}

export interface PrimaryAction {
  label: string;
  onClick: (payload: MarkingUserResponse) => void;
  loading?: boolean;
}

export interface PdfMarkingWorkspaceProps {
  pdfRef: string;
  language: string;
  /** Surfaced run/advance error (run-mode); standalone passes nothing. */
  error?: string | null;
  /** Optional footer CTA (run-mode: "Generate study set"; study-mode: the
   *  forward-compat "Generate a study set from these marks"). Omit to hide it. */
  primaryAction?: PrimaryAction | null;
  /** Footer note under the marks sidebar. */
  footerNote?: string;
  /** Fires (debounced) whenever the saved mark counts change — the Study library
   *  uses it to keep its per-PDF mark count + lastOpened metadata fresh. */
  onMarksChange?: (counts: { deep: number; lookups: number; highlights: number }) => void;
  /** Both modes render the SAME unified popover (colour · study level · look-ups
   *  Explain/Define/Build-intuition/Ask). "deep" (Projects) additionally surfaces
   *  two look-ups wired to the same async model: "Explain in depth" (specialist
   *  card) + "Plot a function" (plot agent). */
  lookupMode?: "all" | "deep";
  /** Server doc/run/file id → attributes each look-up's cost to it on the
   *  backend and scopes the live "marking cost" readout. Omit to hide the chip. */
  documentRef?: string;
}

export function PdfMarkingWorkspace({
  pdfRef,
  language,
  error,
  primaryAction,
  footerNote,
  onMarksChange,
  lookupMode = "all",
  documentRef,
}: PdfMarkingWorkspaceProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const textLayerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const docRef = useRef<PDFDocumentProxy | null>(null);
  const renderTasks = useRef<RenderTask[]>([]);

  const [baseSizes, setBaseSizes] = useState<{ w: number; h: number }[]>([]); // scale=1 dims
  const [fitWidth, setFitWidth] = useState(DEFAULT_FIT);
  const [zoom, setZoom] = useState(1);
  // Interaction mode:
  //  • "scroll"    — a finger scrolls/pinch-zooms; pen/mouse open the action popover.
  //  • "mark"      — a finger draws too; a completed drag/selection opens the popover.
  //  • "highlight" — a completed drag/selection instantly becomes a plain highlight
  //                  (no popover, no LLM). Apple Pencil + mouse always draw.
  // A `modeRef` mirrors it for the document-level mouseup selection listener,
  // which is registered once and would otherwise close over a stale value.
  const [mode, setMode] = useState<"scroll" | "mark" | "highlight">("scroll");
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Responsive page width: fit the measured container, scaled by zoom.
  const displayWidth = Math.max(240, Math.round(fitWidth * zoom));
  const pageSizes = useMemo(
    () => baseSizes.map((b) => ({ w: displayWidth, h: Math.round((displayWidth * b.h) / b.w) })),
    [baseSizes, displayWidth],
  );

  const [deep, setDeep] = useState<DeepMarkLocal[]>([]);
  const [lookups, setLookups] = useState<LookupLocal[]>([]);
  const [highlights, setHighlights] = useState<HighlightLocal[]>([]);
  // Undo/redo history for removed marks (lookups / deep / highlights). A removal
  // pushes the item + its original index onto `removedStack` (and clears the redo
  // future); undo restores it and moves it to `redoStack`; redo re-removes it.
  // Toolbar ↶/↷ buttons + Cmd/Ctrl+Z / Cmd/Ctrl+Shift+Z expose both.
  const [removedStack, setRemovedStack] = useState<RemovedEntry[]>([]);
  const [redoStack, setRedoStack] = useState<RemovedEntry[]>([]);
  const [undoToast, setUndoToast] = useState<string | null>(null);
  const undoToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Per-lookup model pick — the popover dropdown. "" = Auto (default fast model).
  const [markModel, setMarkModel] = useState<string>(loadMarkModel);
  const manifestQ = useQuery({
    queryKey: ["ai-manifest"],
    queryFn: getManifest,
    staleTime: 5 * 60 * 1000,
  });
  // Auto + each available provider's fast (snappy) and flagship (deep) model.
  const modelOptions = useMemo(() => {
    const out: { value: string; label: string }[] = [];
    for (const p of manifestQ.data?.providers ?? []) {
      if (!p.available) continue;
      const seen = new Set<string>();
      for (const [m, tier] of [
        [p.models.fast, "fast"],
        [p.models.flagship, "deep"],
      ] as const) {
        if (!m?.model || seen.has(m.model)) continue;
        seen.add(m.model);
        out.push({ value: `${p.key}::${m.model}`, label: `${p.display_name} · ${m.model} (${tier})` });
      }
    }
    return out;
  }, [manifestQ.data]);
  const pickModel = useCallback((v: string) => {
    setMarkModel(v);
    try {
      localStorage.setItem(MARK_MODEL_KEY, v);
    } catch {
      /* private mode / no storage — keep the in-memory pick */
    }
  }, []);
  const [pending, setPending] = useState<Pending | null>(null);
  const [detail, setDetail] = useState<LookupLocal | null>(null);
  const [askFor, setAskFor] = useState<Pending | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedMarkId, setSelectedMarkId] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // One-time document understanding: compact bullets grounding lookups + the
  // generation payload. Resolved once per PDF (cached in IndexedDB).
  const [docContext, setDocContext] = useState("");
  const [understanding, setUnderstanding] = useState(false);
  // Honest coverage/truncation notice when a doc exceeds the text cap or the
  // image-fallback page cap (never truncate silently). Dismissible.
  const [understandNote, setUnderstandNote] = useState<string | null>(null);
  // Live "marking cost" readout — the accumulated USD spend on this document's
  // interactive look-ups + the automatic understand/segment. Seeded from the
  // server summary, then incremented per look-up. Best-effort: null = unknown.
  const [markCost, setMarkCost] = useState<number | null>(null);

  // Fetch the authoritative accumulated cost for this document (best-effort —
  // any failure leaves the readout as-is). Captures the automatic understand /
  // segment spend the client never sees a per-call cost for.
  const seedCost = useCallback(() => {
    if (!documentRef) return;
    void getMarkingUsageSummary(documentRef)
      .then((res) => setMarkCost(res.total_cost_usd))
      .catch(() => {
        /* best-effort — leave the readout unchanged */
      });
  }, [documentRef]);

  // Seed on mount / whenever the document changes.
  useEffect(() => {
    setMarkCost(null);
    seedCost();
  }, [documentRef, seedCost]);

  // Re-seed once the one-time understand effect resolves (understanding goes
  // true→false), so the automatic understand/segment cost lands in the readout.
  useEffect(() => {
    if (!understanding) seedCost();
  }, [understanding, seedCost]);

  // ── Load the PDF (rendering happens in the width-keyed effect below) ──────
  useEffect(() => {
    let cancelled = false;
    if (!pdfRef) {
      setLoadError(t("This PDF isn't on this device (it was started elsewhere)."));
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const blob = await loadPdf(pdfRef);
        if (!blob) throw new Error(t("PDF not found locally."));
        const buf = await blob.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
        const sizes: { w: number; h: number }[] = [];
        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n);
          const vp = page.getViewport({ scale: 1 });
          sizes.push({ w: vp.width, h: vp.height });
        }
        if (cancelled) return;
        docRef.current = doc;
        setBaseSizes(sizes);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : t("Couldn't open the PDF."));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfRef, t]);

  // ── Measure the container width → fit-to-container (robust on any screen) ──
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let t: number | undefined;
    const measure = () => {
      const w = Math.max(240, el.clientWidth - FIT_PADDING);
      setFitWidth((prev) => (Math.abs(prev - w) > 8 ? w : prev));
    };
    measure();
    const ro = new ResizeObserver(() => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(measure, 120);
    });
    ro.observe(el);
    return () => {
      if (t) window.clearTimeout(t);
      ro.disconnect();
    };
  }, [loading]);

  // ── Render every page at the current display width (re-runs on resize/zoom) ─
  useEffect(() => {
    const doc = docRef.current;
    if (!doc || baseSizes.length === 0) return;
    let cancelled = false;
    renderTasks.current.forEach((task) => task.cancel());
    renderTasks.current = [];
    const dpr = Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, DPR_CAP);
    const renderAll = async () => {
      for (let n = 1; n <= doc.numPages; n++) {
        if (cancelled) return;
        const canvas = canvasRefs.current.get(n);
        const base = baseSizes[n - 1];
        if (!canvas || !base) continue;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        const page = await doc.getPage(n);
        if (cancelled) return;
        const vp = page.getViewport({ scale: (displayWidth * dpr) / base.w });
        canvas.width = Math.round(vp.width);
        canvas.height = Math.round(vp.height);
        const task = page.render({ canvasContext: ctx, viewport: vp });
        renderTasks.current.push(task);
        try {
          await task.promise;
        } catch {
          /* cancelled render — ignore */
        }
        if (cancelled) return;
        // Text layer at the CSS-display scale (no dpr) so selectable spans line
        // up with what the user sees. Best-effort — scanned PDFs have no text.
        const tlContainer = textLayerRefs.current.get(n);
        if (tlContainer) {
          tlContainer.replaceChildren();
          const cssScale = displayWidth / base.w;
          tlContainer.style.setProperty("--scale-factor", String(cssScale));
          try {
            const textLayer = new pdfjsLib.TextLayer({
              textContentSource: await page.getTextContent(),
              container: tlContainer,
              viewport: page.getViewport({ scale: cssScale }),
            });
            await textLayer.render();
          } catch {
            /* no extractable text on this page — region snip still works */
          }
        }
      }
    };
    void renderAll();
    return () => {
      cancelled = true;
      renderTasks.current.forEach((task) => task.cancel());
      renderTasks.current = [];
    };
  }, [displayWidth, baseSizes]);

  // ── Resume saved marks ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdfRef) return;
    void loadMarks<Saved>(pdfRef).then((s) => {
      if (s) {
        setDeep(s.deep ?? []);
        // A lookup persisted mid-flight (status "loading") can't resume its
        // in-memory promise, so surface it as retryable rather than a stuck spinner.
        setLookups(
          (s.lookups ?? []).map((l) =>
            l.status === "loading" ? { ...l, status: "error" as const } : l,
          ),
        );
        setHighlights(s.highlights ?? []);
      }
    });
  }, [pdfRef]);

  // ── One-time document understanding ────────────────────────────────────────
  // Whenever a PDF becomes markable, read it once → compact contextual bullets,
  // cached per ref. Those bullets ground both live lookups (as `context`) and
  // generation image-questions (via the marking payload). Best-effort: any
  // failure just leaves lookups image-only, as before.
  useEffect(() => {
    if (!pdfRef || loading) return;
    let cancelled = false;
    (async () => {
      const cached = await loadUnderstanding(pdfRef);
      if (cached) {
        if (!cancelled) {
          setDocContext(cached.contextText);
          setUnderstandNote(cached.note ?? null);
        }
        return;
      }
      const doc = docRef.current;
      if (!doc) return;
      setUnderstanding(true);
      try {
        // Extract the whole document's text up to the 120k cap.
        let text = "";
        let lastPageRead = 0;
        for (let n = 1; n <= doc.numPages; n++) {
          if (text.length >= 120_000) break;
          const page = await doc.getPage(n);
          const tc = await page.getTextContent();
          text +=
            tc.items
              .map((it) => ("str" in it ? it.str : ""))
              .join(" ")
              .replace(/\s+\n/g, "\n") + "\n";
          lastPageRead = n;
        }
        if (cancelled) return;
        text = text.trim();

        let result: UnderstandResponse;
        let note: string | null = null;
        if (text.length >= 200) {
          // Text path — whole document up to 120k chars.
          if (lastPageRead < doc.numPages) {
            note = t(
              "Large document — context was built from the first ~{{read}} of {{total}} pages (text limit). Every page is still markable.",
              { read: lastPageRead, total: doc.numPages },
            );
          }
          result = await understand({ sourceText: text.slice(0, 120_000), language, documentRef });
        } else {
          // Image fallback — scanned / handwritten / image-only PDF (no text).
          const pageCount = Math.min(4, doc.numPages);
          const imgs = (
            await Promise.all(
              Array.from({ length: pageCount }, (_, i) => renderPageToB64(doc, i + 1)),
            )
          ).filter((x): x is { b64: string; mime: string } => !!x);
          if (cancelled || imgs.length === 0) return;
          if (doc.numPages > pageCount) {
            note = t(
              "Scanned/handwritten document — context was built from the first {{read}} of {{total}} pages. Every page is still markable.",
              { read: pageCount, total: doc.numPages },
            );
          }
          result = await understand({ sourceText: "", language, images: imgs, documentRef });
        }
        if (cancelled) return;
        const contextText = buildContextText(result);
        if (!contextText) return; // produced nothing — leave lookups image-only
        await saveUnderstanding(pdfRef, { ...result, contextText, note: note ?? undefined });
        if (!cancelled) {
          setDocContext(contextText);
          setUnderstandNote(note);
        }
      } catch {
        /* understanding is best-effort — lookups still work image-only */
      } finally {
        if (!cancelled) setUnderstanding(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfRef, loading, language, t, documentRef]);

  const persist = useCallback(
    (d: DeepMarkLocal[], l: LookupLocal[], h: HighlightLocal[] = highlights) => {
      if (pdfRef) void saveMarks<Saved>(pdfRef, { deep: d, lookups: l, highlights: h });
    },
    [pdfRef, highlights],
  );

  // Debounced auto-persist — catches live drag/resize/recolor mutations that
  // update state via functional setState without calling persist() per frame.
  useEffect(() => {
    if (!pdfRef || loading) return;
    const t = window.setTimeout(() => {
      void saveMarks<Saved>(pdfRef, { deep, lookups, highlights });
      setSavedAt(Date.now());
      onMarksChange?.({ deep: deep.length, lookups: lookups.length, highlights: highlights.length });
    }, 300);
    return () => window.clearTimeout(t);
  }, [deep, lookups, highlights, pdfRef, loading, onMarksChange]);

  // Live (no-persist) rect update during a drag/resize; the effect above persists.
  const liveSetRect = (id: string, rect: NormRect) => {
    setDeep((ds) => ds.map((d) => (d.id === id ? { ...d, rect } : d)));
    setLookups((ls) => ls.map((l) => (l.id === id ? { ...l, rect } : l)));
    setHighlights((hs) => hs.map((h) => (h.id === id ? { ...h, rect } : h)));
  };

  const setMarkColor = (id: string, color: string) => {
    setDeep((ds) => ds.map((d) => (d.id === id ? { ...d, color } : d)));
    setLookups((ls) => ls.map((l) => (l.id === id ? { ...l, color } : l)));
    setHighlights((hs) => hs.map((h) => (h.id === id ? { ...h, color } : h)));
  };

  // Move or resize a selected mark; re-snips the region on release so the
  // stored image matches the new box. Normalized against the page canvas rect
  // → scale/zoom-independent.
  const startEdit = (
    id: string,
    pageNum: number,
    editMode: "move" | "resize",
    e: ReactPointerEvent<HTMLElement>,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const canvas = canvasRefs.current.get(pageNum);
    const orig = [...deep, ...lookups, ...highlights].find((m) => m.id === id);
    if (!canvas || !orig) return;
    const cr = canvas.getBoundingClientRect();
    const start = { ...orig.rect };
    const sx = e.clientX;
    const sy = e.clientY;
    let last = start;
    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - sx) / cr.width;
      const dy = (ev.clientY - sy) / cr.height;
      last =
        editMode === "move"
          ? {
              x: Math.min(Math.max(0, start.x + dx), 1 - start.w),
              y: Math.min(Math.max(0, start.y + dy), 1 - start.h),
              w: start.w,
              h: start.h,
            }
          : {
              x: start.x,
              y: start.y,
              w: Math.min(Math.max(0.02, start.w + dx), 1 - start.x),
              h: Math.min(Math.max(0.02, start.h + dy), 1 - start.y),
            };
      liveSetRect(id, last);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      const img = snip(pageNum, last);
      if (img) {
        setDeep((ds) => ds.map((d) => (d.id === id ? { ...d, imageB64: img } : d)));
        setLookups((ls) => ls.map((l) => (l.id === id ? { ...l, imageB64: img } : l)));
        setHighlights((hs) => hs.map((h) => (h.id === id ? { ...h, imageB64: img } : h)));
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  // ── Snip a region from a page canvas → base64 PNG ─────────────────────────
  const snip = (pageNum: number, rect: NormRect): string | null => {
    const canvas = canvasRefs.current.get(pageNum);
    if (!canvas) return null;
    const sx = rect.x * canvas.width;
    const sy = rect.y * canvas.height;
    const sw = rect.w * canvas.width;
    const sh = rect.h * canvas.height;
    if (sw < 4 || sh < 4) return null;
    const tmp = document.createElement("canvas");
    tmp.width = Math.round(sw);
    tmp.height = Math.round(sh);
    const ctx = tmp.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, tmp.width, tmp.height);
    return stripB64(tmp.toDataURL("image/png"));
  };

  // ── Plain highlight — no popover, no LLM ──────────────────────────────────
  // The highlighter path: turn a completed drag/selection straight into a
  // visual-only HighlightLocal. Uses a functional setHighlights (safe from the
  // once-registered mouseup listener's stale closures); the debounced effect
  // above persists + reports the new count.
  const createHighlight = (p: {
    page: number;
    rect: NormRect;
    rects?: NormRect[];
    selectedText?: string;
    imageB64: string;
  }): string => {
    const mark: HighlightLocal = {
      id: uid(),
      page: p.page,
      rect: p.rect,
      rects: p.rects,
      selectedText: p.selectedText,
      imageB64: p.imageB64,
      color: HIGHLIGHT_COLOR,
    };
    setHighlights((hs) => [...hs, mark]);
    return mark.id;
  };

  // ── Drag-to-snip on a page overlay ────────────────────────────────────────
  const onPageDrag = (pageNum: number, e: ReactPointerEvent<HTMLDivElement>) => {
    // A finger only draws in Mark/Highlight mode; in Scroll mode let the browser
    // scroll / pinch-zoom. Apple Pencil ("pen") and mouse always draw.
    if (e.pointerType === "touch" && mode === "scroll") return;
    e.preventDefault();
    setSelectedMarkId(null); // clicking empty page area deselects
    const overlay = e.currentTarget;
    const r = overlay.getBoundingClientRect();
    const startX = e.clientX - r.left;
    const startY = e.clientY - r.top;
    const sel = document.createElement("div");
    sel.style.cssText =
      "position:absolute;border:2px solid #ea580c;background:rgba(234,88,12,0.12);pointer-events:none;z-index:5;";
    overlay.appendChild(sel);
    let curX = startX;
    let curY = startY;
    const move = (ev: PointerEvent) => {
      curX = ev.clientX - r.left;
      curY = ev.clientY - r.top;
      const x = Math.min(startX, curX);
      const y = Math.min(startY, curY);
      sel.style.left = `${x}px`;
      sel.style.top = `${y}px`;
      sel.style.width = `${Math.abs(curX - startX)}px`;
      sel.style.height = `${Math.abs(curY - startY)}px`;
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      overlay.removeChild(sel);
      const x = Math.min(startX, curX);
      const y = Math.min(startY, curY);
      const w = Math.abs(curX - startX);
      const h = Math.abs(curY - startY);
      if (w < 6 || h < 6) return;
      const rect: NormRect = { x: x / r.width, y: y / r.height, w: w / r.width, h: h / r.height };
      const img = snip(pageNum, rect);
      if (!img) return;
      if (mode === "highlight") {
        createHighlight({ page: pageNum, rect, imageB64: img });
        return;
      }
      // Mark path (both modes): a gesture becomes a plain highlight straight
      // away, then the unified popover (recolour / study-level / look-up) acts on
      // it. Projects ("deep") gets the same popover with two extra look-ups.
      const id = createHighlight({ page: pageNum, rect, imageB64: img });
      setSelectedMarkId(id);
      setPending({ page: pageNum, rect, imageB64: img, anchorTop: y + h, anchorLeft: x, markId: id });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  // ── Text-layer selection → pending ────────────────────────────────────────
  // A drag across the transparent text layer makes a native selection. On
  // mouseup we capture the real text + per-line rects (the wrapped-highlight
  // shape) and snip the bounding box, then open the same action popover.
  useEffect(() => {
    const onMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
      const text = sel.toString().trim();
      if (!text) return;
      // Find the page via the .textLayer container holding the anchor node.
      let node: Node | null = sel.anchorNode;
      let tl: HTMLElement | null = null;
      while (node) {
        if (node instanceof HTMLElement && node.classList.contains("textLayer")) {
          tl = node;
          break;
        }
        node = node.parentNode;
      }
      const pageNum = tl ? Number(tl.dataset.page || 0) : 0;
      if (!tl || !pageNum) return;
      const cRect = tl.getBoundingClientRect();
      if (cRect.width < 1 || cRect.height < 1) return;
      const clientRects = Array.from(sel.getRangeAt(0).getClientRects()).filter(
        (r) => r.width > 1 && r.height > 1,
      );
      if (clientRects.length === 0) return;
      const rects: NormRect[] = clientRects.map((r) => ({
        x: (r.left - cRect.left) / cRect.width,
        y: (r.top - cRect.top) / cRect.height,
        w: r.width / cRect.width,
        h: r.height / cRect.height,
      }));
      const minX = Math.min(...rects.map((r) => r.x));
      const minY = Math.min(...rects.map((r) => r.y));
      const maxX = Math.max(...rects.map((r) => r.x + r.w));
      const maxY = Math.max(...rects.map((r) => r.y + r.h));
      const bbox: NormRect = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
      const img = snip(pageNum, bbox);
      if (!img) return;
      // Highlight mode: a selection becomes a plain highlight straight away —
      // no popover, no LLM. `modeRef` reads the live mode (this listener is
      // registered once, so it can't close over the `mode` state directly).
      if (modeRef.current === "highlight") {
        createHighlight({ page: pageNum, rect: bbox, rects, selectedText: text, imageB64: img });
        setSelectedMarkId(null);
        sel.removeAllRanges(); // clear the native selection so the highlight shows through
        return;
      }
      const sz = pageSizes[pageNum - 1];
      // Mark path (both modes): create the highlight up front, select it, and
      // anchor the unified popover to it (see onPageDrag). `lookupMode` is fixed
      // per mount, so closing over it in this once-registered listener is safe.
      const id = createHighlight({ page: pageNum, rect: bbox, rects, selectedText: text, imageB64: img });
      sel.removeAllRanges();
      setSelectedMarkId(id);
      setPending({
        page: pageNum,
        rect: bbox,
        rects,
        selectedText: text,
        imageB64: img,
        anchorTop: sz ? maxY * sz.h : 0,
        anchorLeft: sz ? minX * sz.w : 0,
        markId: id,
      });
    };
    document.addEventListener("mouseup", onMouseUp);
    return () => document.removeEventListener("mouseup", onMouseUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSizes]);

  // ── Actions ───────────────────────────────────────────────────────────────

  // A lookup is "ready" (openable in the detail modal) unless it's mid-flight or
  // failed. A missing status is a legacy/blocking-path lookup → ready.
  const isReady = (l: LookupLocal) => l.status !== "loading" && l.status !== "error";
  const openDetail = (l: LookupLocal) => {
    if (isReady(l)) setDetail(l);
  };

  // ── Background lookups ────────────────────────────────────────────────────
  // Fire the backend call for an already-created loading lookup, keyed by id so
  // several can run concurrently without clobbering each other. Settles the row
  // to "ready" or "error"; the debounced auto-persist effect saves after each
  // settle. `resolver` picks which call runs (default the shallow /explain):
  //   • "explain" → /explain           → explanation blurb.
  //   • "plot"    → /plot (agent)       → rendered chart URL + caption (kind "plot").
  //   • "card"    → /explain-card       → specialist facets + composed body.
  // A missing status on the settled row counts as "ready".
  const markError = (id: string, msg?: string) =>
    setLookups((ls) =>
      ls.map((l) =>
        // keep:false so a failed lookup isn't dead weight in the payload / saved
        // marks (it still shows with Retry until then).
        l.id === id ? { ...l, status: "error" as const, errorMsg: msg, keep: false } : l,
      ),
    );
  const fireLookup = (
    id: string,
    params: {
      kind: LookupKind;
      question?: string;
      selectedText?: string;
      imageB64: string;
      resolver?: "explain" | "plot" | "card";
    },
  ) => {
    const resolver = params.resolver ?? "explain";
    if (resolver === "plot") {
      // Sandboxed plot agent — keep the rendered chart URL (imageUrl) + caption.
      void callWithRetry(() =>
        plot({
          language,
          selectedText: params.selectedText,
          context: docContext || null,
          imageB64: params.imageB64,
          documentRef,
        }),
      )
        .then((resp) => {
          if (resp.cost_usd != null) setMarkCost((c) => (c ?? 0) + resp.cost_usd!);
          setLookups((ls) =>
            ls.map((l) =>
              l.id === id
                ? {
                    ...l,
                    kind: "plot" as const,
                    explanation: resp.caption,
                    imageUrl: resp.image_url,
                    status: "ready" as const,
                    keep: true, // a settled lookup is included again (undoes an error keep:false)
                  }
                : l,
            ),
          );
        })
        .catch((err) => markError(id, errText(err)));
      return;
    }
    if (resolver === "card") {
      // Specialist deep card — store each facet on its own field. `explanation`
      // holds the PURE explanation facet (NOT a composed blob): the detail modal
      // renders the facets as labelled sections, and the backend fold
      // (`_card_body`) wraps this explanation between the Definition / Intuition /
      // Check-yourself sections — so pre-composing here would duplicate them.
      void callWithRetry(() =>
        explainCard({
          language,
          selectedText: params.selectedText,
          context: docContext || null,
          documentRef,
        }),
      )
        .then((card) => {
          if (card.cost_usd != null) setMarkCost((c) => (c ?? 0) + card.cost_usd!);
          setLookups((ls) =>
            ls.map((l) =>
              l.id === id
                ? {
                    ...l,
                    kind: "general" as const,
                    explanation: card.explanation,
                    definition: card.definition,
                    intuition: card.intuition,
                    question: card.question,
                    answer: card.answer,
                    status: "ready" as const,
                    keep: true, // a settled lookup is included again (undoes an error keep:false)
                  }
                : l,
            ),
          );
        })
        .catch((err) => markError(id, errText(err)));
      return;
    }
    const [pickedProvider, ...pickedRest] = markModel ? markModel.split("::") : [];
    void callWithRetry(() =>
      explain({
        kind: params.kind,
        language,
        question: params.question,
        selectedText: params.selectedText,
        context: docContext || null,
        imageB64: params.imageB64,
        documentRef,
        provider: pickedProvider || null,
        model: pickedRest.length ? pickedRest.join("::") : null,
      }),
    )
      .then((resp) => {
        if (resp.cost_usd != null) setMarkCost((c) => (c ?? 0) + resp.cost_usd!);
        setLookups((ls) =>
          ls.map((l) =>
            l.id === id
              ? { ...l, explanation: resp.explanation, status: "ready" as const, keep: true }
              : l,
          ),
        );
      })
      .catch((err) => markError(id, errText(err)));
  };

  // Create a loading lookup, drop it into `lookups` immediately, close the
  // popover, and fire the request in the background (NOT awaited by the caller).
  const runLookup = (
    params: {
      kind: LookupKind;
      question?: string;
      selectedText?: string;
      imageB64: string;
      page: number;
      rect: NormRect;
      rects?: NormRect[];
      resolver?: "explain" | "plot" | "card";
    },
    opts?: { color?: string },
  ) => {
    const id = uid();
    const mark: LookupLocal = {
      id,
      kind: params.kind,
      question: params.question,
      selectedText: params.selectedText,
      explanation: "",
      page: params.page,
      rect: params.rect,
      rects: params.rects,
      imageB64: params.imageB64,
      keep: true,
      color: opts?.color,
      status: "loading",
      resolver: params.resolver ?? "explain",
    };
    setLookups((ls) => [...ls, mark]);
    setPending(null);
    fireLookup(id, params);
  };

  // Re-run a failed (or any) lookup with its stored request params.
  const retryLookup = (id: string) => {
    const l = lookups.find((x) => x.id === id);
    if (!l) return;
    setLookups((ls) => ls.map((x) => (x.id === id ? { ...x, status: "loading" as const } : x)));
    // Re-fire the SAME resolver so a failed plot retries the plot, a failed card
    // retries the card, etc. (missing resolver → "explain", per fireLookup).
    fireLookup(id, {
      kind: l.kind,
      question: l.question,
      selectedText: l.selectedText,
      imageB64: l.imageB64,
      resolver: l.resolver,
    });
  };

  // Popover glue: a look-up button fires a background lookup from the pending
  // gesture. The plain highlight the gesture created is consumed (removed) — its
  // colour carries over to the lookup — so we never leave a duplicate mark.
  const fireLookupFromPending = (
    p: Pending,
    kind: LookupKind,
    question?: string,
    opts?: { resolver?: "explain" | "plot" | "card" },
  ) => {
    const anchorColor = p.markId ? highlights.find((h) => h.id === p.markId)?.color : undefined;
    if (p.markId) removeHighlight(p.markId);
    runLookup(
      {
        kind,
        question,
        selectedText: p.selectedText,
        imageB64: p.imageB64,
        page: p.page,
        rect: p.rect,
        rects: p.rects,
        resolver: opts?.resolver,
      },
      { color: anchorColor },
    );
  };

  // Record a removed mark for undo + flash the snackbar (auto-hides after 6s). A
  // fresh removal invalidates any redo future.
  const pushRemoved = (entry: RemovedEntry, label: string) => {
    setRemovedStack((s) => [...s, entry].slice(-10));
    setRedoStack([]);
    if (undoToastTimer.current) clearTimeout(undoToastTimer.current);
    setUndoToast(label);
    undoToastTimer.current = setTimeout(() => setUndoToast(null), 6000);
  };
  // Splice a removed mark back into its array at the given index + re-persist.
  const restoreEntry = (entry: RemovedEntry) => {
    if (entry.kind === "lookup") {
      const next = lookups.slice();
      next.splice(Math.min(entry.index, next.length), 0, entry.item);
      setLookups(next);
      persist(deep, next);
    } else if (entry.kind === "deep") {
      const next = deep.slice();
      next.splice(Math.min(entry.index, next.length), 0, entry.item);
      setDeep(next);
      persist(next, lookups);
    } else {
      const next = highlights.slice();
      next.splice(Math.min(entry.index, next.length), 0, entry.item);
      setHighlights(next);
      persist(deep, lookups, next);
    }
  };
  // Undo (backward): restore the most recent removal and move it to the redo stack.
  const undoRemove = () => {
    if (removedStack.length === 0) return;
    const entry = removedStack[removedStack.length - 1];
    restoreEntry(entry);
    setRemovedStack((s) => s.slice(0, -1));
    setRedoStack((s) => [...s, entry].slice(-10));
    if (undoToastTimer.current) clearTimeout(undoToastTimer.current);
    setUndoToast(null);
  };
  // Redo (forward): re-remove the most recently undone mark and move it back to the
  // undo stack (re-recording its current index, without clearing the redo future).
  const redoRemove = () => {
    if (redoStack.length === 0) return;
    const entry = redoStack[redoStack.length - 1];
    const id = entry.item.id;
    if (entry.kind === "lookup") {
      const index = lookups.findIndex((l) => l.id === id);
      const next = lookups.filter((l) => l.id !== id);
      setLookups(next);
      persist(deep, next);
      if (detail?.id === id) setDetail(null);
      setRemovedStack((s) => [...s, { ...entry, index: index < 0 ? entry.index : index }].slice(-10));
    } else if (entry.kind === "deep") {
      const index = deep.findIndex((d) => d.id === id);
      const next = deep.filter((d) => d.id !== id);
      setDeep(next);
      persist(next, lookups);
      setRemovedStack((s) => [...s, { ...entry, index: index < 0 ? entry.index : index }].slice(-10));
    } else {
      const index = highlights.findIndex((h) => h.id === id);
      const next = highlights.filter((h) => h.id !== id);
      setHighlights(next);
      persist(deep, lookups, next);
      setRemovedStack((s) => [...s, { ...entry, index: index < 0 ? entry.index : index }].slice(-10));
    }
    setRedoStack((s) => s.slice(0, -1));
  };
  // Cmd/Ctrl+Z → undo, Cmd/Ctrl+Shift+Z (or Cmd/Ctrl+Y) → redo. Refs keep the
  // listener bound once while calling the freshest closures. Ignored while typing.
  const undoRef = useRef(undoRemove);
  undoRef.current = undoRemove;
  const redoRef = useRef(redoRemove);
  redoRef.current = redoRemove;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "TEXTAREA" || tag === "INPUT" || el?.isContentEditable) return;
      const isZ = e.key === "z" || e.key === "Z";
      const isY = e.key === "y" || e.key === "Y";
      if (isZ && !e.shiftKey) {
        e.preventDefault();
        undoRef.current();
      } else if ((isZ && e.shiftKey) || isY) {
        e.preventDefault();
        redoRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(
    () => () => {
      if (undoToastTimer.current) clearTimeout(undoToastTimer.current);
    },
    [],
  );
  const removeDeep = (id: string) => {
    const index = deep.findIndex((d) => d.id === id);
    if (index < 0) return;
    const item = deep[index];
    const next = deep.filter((d) => d.id !== id);
    setDeep(next);
    persist(next, lookups);
    pushRemoved({ kind: "deep", item, index }, t("Removed “{{label}}”", { label: item.label }));
  };
  const removeHighlight = (id: string) => {
    const index = highlights.findIndex((h) => h.id === id);
    if (index < 0) return;
    const item = highlights[index];
    const next = highlights.filter((h) => h.id !== id);
    setHighlights(next);
    persist(deep, lookups, next);
    pushRemoved({ kind: "highlight", item, index }, t("Removed highlight"));
  };
  // The ONE study-level control (used by the popover, the mark mini-toolbar, and
  // the sidebar), preserving the mark's id across the highlight ↔ deep boundary:
  //  • depth on a plain highlight → promote it into a depth-set study mark.
  //  • a different depth on an existing study mark → just retune the depth.
  //  • the SAME depth again (or `null`) → demote back to a plain highlight.
  const setMarkLevel = (id: string, depth: Depth | null) => {
    const existingDeep = deep.find((d) => d.id === id);
    // Demote a study mark back to a plain highlight (same id).
    if (depth === null || (existingDeep && existingDeep.depth === depth)) {
      if (!existingDeep) return;
      const h: HighlightLocal = {
        id,
        page: existingDeep.page,
        rect: existingDeep.rect,
        rects: existingDeep.rects,
        selectedText: existingDeep.selectedText,
        imageB64: existingDeep.imageB64,
        color: existingDeep.color,
      };
      const nextDeep = deep.filter((d) => d.id !== id);
      const nextHigh = [...highlights, h];
      setDeep(nextDeep);
      setHighlights(nextHigh);
      persist(nextDeep, lookups, nextHigh);
      return;
    }
    // Retune an existing study mark's depth.
    if (existingDeep) {
      const nextDeep = deep.map((d) => (d.id === id ? { ...d, depth } : d));
      setDeep(nextDeep);
      persist(nextDeep, lookups);
      return;
    }
    // Promote a plain highlight into a study mark (same id).
    const h = highlights.find((x) => x.id === id);
    if (!h) return;
    const mark: DeepMarkLocal = {
      id,
      label: labelFor(h),
      depth,
      page: h.page,
      rect: h.rect,
      rects: h.rects,
      selectedText: h.selectedText,
      imageB64: h.imageB64,
      color: h.color,
    };
    const nextDeep = [...deep, mark];
    const nextHigh = highlights.filter((x) => x.id !== id);
    setDeep(nextDeep);
    setHighlights(nextHigh);
    persist(nextDeep, lookups, nextHigh);
  };
  const removeLookup = (id: string) => {
    const index = lookups.findIndex((l) => l.id === id);
    if (index < 0) return;
    const item = lookups[index];
    const next = lookups.filter((l) => l.id !== id);
    setLookups(next);
    persist(deep, next);
    if (detail?.id === id) setDetail(null);
    pushRemoved(
      { kind: "lookup", item, index },
      t("Removed “{{label}}”", { label: item.question ?? item.kind.replace(/_/g, " ") }),
    );
  };
  // Remove a mark regardless of which array it currently lives in (a mark starts
  // as a highlight, then may become deep or a lookup).
  const removeMark = (id: string) => {
    if (deep.some((d) => d.id === id)) removeDeep(id);
    else if (highlights.some((h) => h.id === id)) removeHighlight(id);
    else removeLookup(id);
  };
  // Open the ONE unified popover for an existing mark (recolour / study level /
  // look-ups / remove), anchored just below it — the same popover the create
  // gesture shows. Deep + highlight marks route here on tap.
  const openMarkMenu = (
    m: DeepMarkLocal | HighlightLocal | LookupLocal,
    pageNum: number,
  ) => {
    const sz = pageSizes[pageNum - 1];
    setSelectedMarkId(m.id);
    setPending({
      page: pageNum,
      rect: m.rect,
      rects: m.rects,
      selectedText: m.selectedText,
      imageB64: m.imageB64,
      markId: m.id,
      anchorTop: sz ? (m.rect.y + m.rect.h) * sz.h : 0,
      anchorLeft: sz ? m.rect.x * sz.w : 0,
    });
  };
  const setLabel = (id: string, label: string) => {
    const next = deep.map((d) => (d.id === id ? { ...d, label } : d));
    setDeep(next);
    persist(next, lookups);
  };
  const toggleDirective = (id: string, key: string) => {
    setDeep((ds) =>
      ds.map((d) => {
        if (d.id !== id) return d;
        const cur = d.directives ?? [];
        return { ...d, directives: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
      }),
    );
  };
  const addCustomDirective = (id: string, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setDeep((ds) => ds.map((d) => (d.id === id ? { ...d, directives: [...(d.directives ?? []), trimmed] } : d)));
  };
  const setLookupText = (id: string, explanation: string) => {
    const next = lookups.map((l) => (l.id === id ? { ...l, explanation } : l));
    setLookups(next);
    persist(deep, next);
    if (detail?.id === id) setDetail({ ...detail, explanation });
  };

  const firePrimary = () => {
    if (!primaryAction) return;
    primaryAction.onClick(buildMarkingPayload(deep, lookups, docContext, highlights));
  };

  // Ready + kept lookups gate the payload / primary CTA (a loading/failed one
  // isn't includable yet).
  const keptLookups = lookups.filter((l) => l.keep && l.explanation);
  // Everything the "Lookups" sidebar shows — also surfaces in-flight + failed
  // rows so the learner sees a spinner / Retry. (deep-path lookups have no
  // status → they only ever hit the ready branch, exactly as before.)
  const sidebarLookups = lookups.filter(
    (l) => l.keep && (l.explanation || l.status === "loading" || l.status === "error"),
  );
  // const captures so non-null narrowing survives into the modal closures.
  const det = detail;
  const ask = askFor;

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center py-20 text-brand-600">
        <Spinner className="h-7 w-7" />
      </div>
    );
  }
  if (loadError && pageSizes.length === 0) {
    return <Alert>{loadError}</Alert>;
  }

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[1fr_340px]">
      {/* PDF + annotation column */}
      <div className="min-w-0">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {/* Undo / redo for removed marks — flush-left, directly left of the tool pills. */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={undoRemove}
              disabled={removedStack.length === 0}
              title={t("Undo")}
              aria-label={t("Undo")}
              className="h-7 w-7 rounded-lg bg-black/5 text-sm hover:bg-black/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ↶
            </button>
            <button
              type="button"
              onClick={redoRemove}
              disabled={redoStack.length === 0}
              title={t("Redo")}
              aria-label={t("Redo")}
              className="h-7 w-7 rounded-lg bg-black/5 text-sm hover:bg-black/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ↷
            </button>
          </div>
          <div className="flex items-center rounded-lg border border-black/10 p-0.5">
            {(
              [
                { key: "scroll", label: `✋ ${t("Scroll")}` },
                { key: "mark", label: `✏️ ${t("Mark")}` },
                { key: "highlight", label: `🖍️ ${t("Highlight")}` },
              ] as const
            ).map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMode(m.key)}
                className={
                  "rounded-md px-2 py-1 text-xs font-medium " +
                  (mode === m.key ? "bg-brand-50 text-brand-700" : "text-ink-faint")
                }
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.15) * 100) / 100))}
              className="h-7 w-7 rounded-lg bg-black/5 text-sm font-bold hover:bg-black/10"
              aria-label={t("Zoom out")}
            >
              −
            </button>
            <span className="w-12 text-center text-xs tabular-nums text-ink-faint">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.15) * 100) / 100))}
              className="h-7 w-7 rounded-lg bg-black/5 text-sm font-bold hover:bg-black/10"
              aria-label={t("Zoom in")}
            >
              +
            </button>
            <button
              type="button"
              onClick={() => setZoom(1)}
              className="rounded-lg px-2 py-1 text-xs text-brand-600 hover:bg-brand-50"
            >
              {t("Fit")}
            </button>
          </div>
          {markCost != null && (
            <span
              className="rounded-lg border border-black/10 px-2 py-1 text-xs tabular-nums text-ink-faint"
              title={t("Marking cost")}
            >
              {t("Marking cost")}: ${markCost.toFixed(4)}
            </span>
          )}
          {understanding && (
            <span className="ml-auto flex items-center gap-1.5 text-xs text-brand-600">
              <Spinner className="h-3.5 w-3.5" /> {t("Reading your document…")}
            </span>
          )}
        </div>
        {understandNote && (
          <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span className="flex-1">{understandNote}</span>
            <button
              type="button"
              onClick={() => setUnderstandNote(null)}
              className="shrink-0 font-bold text-amber-700 hover:text-amber-900"
              aria-label={t("Dismiss")}
            >
              ✕
            </button>
          </div>
        )}
        <div ref={containerRef} className="max-h-[80vh] overflow-auto rounded-xl bg-black/5 p-3">
          <div className="space-y-3">
          {pageSizes.map((sz, idx) => {
            const pageNum = idx + 1;
            const pageMarks = [
              ...deep.filter((d) => d.page === pageNum).map((d) => ({ kind: "deep" as const, m: d })),
              ...lookups.filter((l) => l.page === pageNum).map((l) => ({ kind: "look" as const, m: l })),
              ...highlights.filter((h) => h.page === pageNum).map((h) => ({ kind: "high" as const, m: h })),
            ];
            // `pop` is a const so its non-null narrowing survives into the
            // onClick closures below (TS won't narrow the mutable `pending` there).
            const pop = pending && pending.page === pageNum ? pending : null;
            return (
              <div
                key={pageNum}
                className="relative mx-auto bg-white shadow-soft"
                style={{ width: sz.w, height: sz.h }}
              >
                <canvas
                  ref={(el) => {
                    if (el) canvasRefs.current.set(pageNum, el);
                  }}
                  style={{ width: sz.w, height: sz.h, display: "block" }}
                />
                {/* snip-drag overlay — empty-area drags (sits below the text
                    layer so text spans win the pointer for native selection). */}
                <div
                  className="absolute inset-0 cursor-crosshair"
                  style={{ touchAction: mode === "scroll" ? "auto" : "none" }}
                  onPointerDown={(e) => onPageDrag(pageNum, e)}
                />
                {/* pdf.js text layer — transparent selectable spans (CSS .textLayer) */}
                <div
                  className="textLayer"
                  data-page={pageNum}
                  ref={(el) => {
                    if (el) textLayerRefs.current.set(pageNum, el);
                  }}
                />
                {/* marks + hover + action popover, above the text layer */}
                <div className="absolute inset-0" style={{ pointerEvents: "none", zIndex: 4 }}>
                  {pageMarks.map(({ kind, m }) => {
                    const selected = selectedMarkId === m.id;
                    // Deep marks colour by depth (heat scheme); an explicit
                    // m.color (Fundamental's purple, or a manual colour-picker
                    // choice) still overrides. Highlights default yellow, lookups blue.
                    const col =
                      m.color ??
                      (kind === "deep"
                        ? DEPTH_COLORS[(m as DeepMarkLocal).depth]
                        : kind === "high"
                          ? HIGHLIGHT_COLOR
                          : "#3b82f6");
                    return (
                      <div
                        key={m.id}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          if (selected) startEdit(m.id, pageNum, "move", e);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // A ready lookup opens its full answer; an errored/loading
                          // lookup just selects (its Retry badge stays visible); a
                          // deep/highlight mark opens the ONE unified popover (colours,
                          // study level, look-ups, remove) anchored to it.
                          if (kind === "look") {
                            if (isReady(m as LookupLocal)) openDetail(m as LookupLocal);
                            else setSelectedMarkId(m.id);
                          } else {
                            openMarkMenu(m, pageNum);
                          }
                        }}
                        onDoubleClick={() => kind === "look" && openDetail(m as LookupLocal)}
                        onMouseEnter={() => setHoveredId(m.id)}
                        onMouseLeave={() => setHoveredId((h) => (h === m.id ? null : h))}
                        title={
                          kind === "deep"
                            ? (m as DeepMarkLocal).label
                            : kind === "high"
                              ? (m as HighlightLocal).selectedText || t("Highlight")
                              : t("Tap to open")
                        }
                        style={{
                          position: "absolute",
                          left: `${m.rect.x * 100}%`,
                          top: `${m.rect.y * 100}%`,
                          width: `${m.rect.w * 100}%`,
                          height: `${m.rect.h * 100}%`,
                          // Text marks fill per-line (below); region marks fill the box.
                          background: m.rects?.length ? "transparent" : `${col}3d`,
                          // A text selection (has per-line `rects`) is drawn as its
                          // per-line fills only — the bounding box encloses whitespace
                          // + ragged line ends and reads as a stray square, especially
                          // across a two-column wrap. Border it only when selected (so
                          // the resize handle has context) or when it's a region snip
                          // (equation/figure/image — the rect IS the mark).
                          border:
                            m.rects?.length && !selected
                              ? "none"
                              : `${selected ? 2 : 1.5}px solid ${col}`,
                          cursor: selected ? "move" : "pointer",
                          touchAction: "none",
                          pointerEvents: "auto",
                          boxShadow: selected ? "0 0 0 2px rgba(0,0,0,0.12)" : undefined,
                        }}
                      >
                        {/* per-line highlight fills for a wrapped text selection */}
                        {m.rects?.map((r, ri) => (
                          <div
                            key={ri}
                            style={{
                              position: "absolute",
                              left: `${((r.x - m.rect.x) / m.rect.w) * 100}%`,
                              top: `${((r.y - m.rect.y) / m.rect.h) * 100}%`,
                              width: `${(r.w / m.rect.w) * 100}%`,
                              height: `${(r.h / m.rect.h) * 100}%`,
                              background: `${col}3d`,
                              borderRadius: 2,
                              pointerEvents: "none",
                            }}
                          />
                        ))}
                        {/* Background-lookup status badge (both modes): a
                            spinner while loading, a Retry pill on failure. */}
                        {kind === "look" && (m as LookupLocal).status === "loading" && (
                          <div
                            className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-soft"
                            style={{ pointerEvents: "none" }}
                          >
                            <Spinner className="h-3.5 w-3.5" />
                          </div>
                        )}
                        {kind === "look" && (m as LookupLocal).status === "error" && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              retryLookup(m.id);
                            }}
                            className="absolute -top-3 left-0 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-soft hover:bg-red-600"
                          >
                            {t("Retry")}
                          </button>
                        )}
                        {/* Selection just shows the resize handle; all mark controls
                            (recolour / study level / look-ups / remove) live in the ONE
                            unified popover below. */}
                        {selected && (
                          <div
                            onPointerDown={(e) => startEdit(m.id, pageNum, "resize", e)}
                            style={{
                              position: "absolute",
                              right: -7,
                              bottom: -7,
                              width: 14,
                              height: 14,
                              borderRadius: 7,
                              background: "#fff",
                              border: `2px solid ${col}`,
                              cursor: "nwse-resize",
                              touchAction: "none",
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                  {(() => {
                    const hov = pageMarks.find(({ m }) => m.id === hoveredId);
                    if (!hov) return null;
                    const hm = hov.m;
                    // Lookups reflect their async status in the tooltip: "…" while
                    // loading, a failure note on error, the explanation once ready.
                    const lookupTip = (l: LookupLocal): string => {
                      if (l.status === "loading") return t("Looking this up…");
                      if (l.status === "error")
                        return l.errorMsg || t("Lookup failed — tap Retry.");
                      const body = previewText(l.explanation);
                      return `${l.question ?? l.kind.replace(/_/g, " ")}: ${body.slice(0, 160)}${body.length > 160 ? "…" : ""}`;
                    };
                    const text =
                      hov.kind === "deep"
                        ? `${(hm as DeepMarkLocal).label} · ${t(DEPTH_LABELS[(hm as DeepMarkLocal).depth])}`
                        : hov.kind === "high"
                          ? (hm as HighlightLocal).selectedText || t("Highlight")
                          : lookupTip(hm as LookupLocal);
                    return (
                      <div
                        className="pointer-events-none absolute z-20 max-w-xs rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs leading-snug text-white shadow-soft"
                        style={{
                          left: `${hm.rect.x * 100}%`,
                          top: `calc(${(hm.rect.y + hm.rect.h) * 100}% + 4px)`,
                        }}
                      >
                        {text}
                      </div>
                    );
                  })()}
                  {pop && (
                    // ── Unified popover — recolour · study-level · look-up.
                    // Every button acts on pop.markId (the highlight the gesture
                    // just created); lookups fire in the background, non-blocking.
                    // Projects ("deep") gets two extra look-ups (Explain in depth
                    // + Plot a function) wired to the same async/status model.
                    (() => {
                      const popMark = [...highlights, ...deep].find((mk) => mk.id === pop.markId);
                      const popDepth = deep.find((d) => d.id === pop.markId)?.depth ?? null;
                      const activeColor = popMark?.color ?? HIGHLIGHT_COLOR;
                      return (
                        <div
                          className="absolute z-10 rounded-lg border border-black/10 bg-white p-2 shadow-soft"
                          style={{ top: pop.anchorTop + 4, left: pop.anchorLeft, pointerEvents: "auto" }}
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <div className="flex w-64 flex-col gap-2">
                            {/* Header — the ✕ discards this mark AND closes the popover. */}
                            <div className="flex items-center justify-between px-0.5">
                              <span className="text-[11px] font-semibold uppercase text-ink-faint">
                                {t("Mark")}
                              </span>
                              <button
                                type="button"
                                title={t("Remove")}
                                aria-label={t("Remove")}
                                onClick={() => {
                                  if (pop.markId) removeMark(pop.markId);
                                  setPending(null);
                                  setSelectedMarkId(null);
                                }}
                                className="rounded px-1 text-sm font-bold text-red-500 hover:bg-red-50"
                              >
                                ✕
                              </button>
                            </div>
                            {/* Recolour the mark. */}
                            <div className="flex items-center gap-1.5 px-0.5">
                              {MARK_COLORS.map((c) => (
                                <button
                                  key={c}
                                  type="button"
                                  onClick={() => pop.markId && setMarkColor(pop.markId, c)}
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    background: c,
                                    border: c === activeColor ? "2px solid #111" : "1px solid rgba(0,0,0,0.2)",
                                  }}
                                />
                              ))}
                            </div>

                            {/* Study level — optional; unset = plain highlight. */}
                            <div>
                              <span className="px-0.5 text-[11px] font-semibold uppercase text-ink-faint">
                                {t("Study level")}
                              </span>
                              <div className="mt-1 flex gap-1">
                                {DEPTHS.map((dp) => (
                                  <button
                                    key={dp}
                                    type="button"
                                    className={`flex-1 rounded px-2 py-1.5 text-sm font-semibold ${
                                      popDepth === dp ? "ring-2 ring-inset ring-black/40 " : ""
                                    }${DEPTH_BTN_CLASSES[dp]}`}
                                    onClick={() => pop.markId && setMarkLevel(pop.markId, dp)}
                                  >
                                    {t(DEPTH_LABELS[dp])}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Look up — background, non-blocking lookups. */}
                            <div>
                              <span className="px-0.5 text-[11px] font-semibold uppercase text-ink-faint">
                                {t("Look up")}
                              </span>
                              {/* Per-lookup model — applies to Explain / Define /
                                  Build intuition / Ask. "Auto" = the default fast
                                  model; a stronger model is slower but deeper. */}
                              {modelOptions.length > 0 && (
                                <select
                                  className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1 text-xs text-ink shadow-sm focus:border-brand-400 focus:outline-none"
                                  value={markModel}
                                  onChange={(e) => pickModel(e.target.value)}
                                  title={t("Model for Explain / Define / Build intuition / Ask")}
                                >
                                  <option value="">{t("Auto (my setting)")}</option>
                                  {modelOptions.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              )}
                              <div className="mt-1 grid grid-cols-2 gap-1">
                                <button
                                  type="button"
                                  className="rounded px-2 py-1 text-sm hover:bg-brand-50"
                                  onClick={() => fireLookupFromPending(pop, "general")}
                                >
                                  {t("Explain")}
                                </button>
                                <button
                                  type="button"
                                  className="rounded px-2 py-1 text-sm hover:bg-brand-50"
                                  onClick={() => fireLookupFromPending(pop, "definition")}
                                >
                                  {t("Define")}
                                </button>
                                <button
                                  type="button"
                                  className="rounded px-2 py-1 text-sm hover:bg-brand-50"
                                  onClick={() => fireLookupFromPending(pop, "intuition")}
                                >
                                  {t("Build intuition")}
                                </button>
                                <button
                                  type="button"
                                  className="rounded px-2 py-1 text-sm hover:bg-brand-50"
                                  onClick={() => {
                                    setAskFor(pop);
                                    setPending(null);
                                  }}
                                >
                                  {t("Ask…")}
                                </button>
                                {/* Projects-only deep look-ups — the specialist
                                    card + the plot agent, both non-blocking. */}
                                {lookupMode === "deep" && (
                                  <>
                                    <button
                                      type="button"
                                      className="rounded px-2 py-1 text-sm hover:bg-brand-50"
                                      onClick={() =>
                                        fireLookupFromPending(pop, "general", undefined, {
                                          resolver: "card",
                                        })
                                      }
                                    >
                                      {t("Explain in depth")}
                                    </button>
                                    <button
                                      type="button"
                                      className="rounded px-2 py-1 text-sm hover:bg-brand-50"
                                      onClick={() =>
                                        fireLookupFromPending(pop, "plot", undefined, {
                                          resolver: "plot",
                                        })
                                      }
                                    >
                                      {t("Plot a function")}
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            <button
                              type="button"
                              className="mt-0.5 rounded px-2 py-1 text-center text-xs font-medium text-ink-faint hover:bg-black/5"
                              onClick={() => {
                                setPending(null);
                                setSelectedMarkId(null);
                              }}
                            >
                              {t("Done")}
                            </button>
                          </div>
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>

      {/* Marks sidebar — sticky on desktop so it stays in view as the PDF scrolls. */}
      <div className="space-y-4 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:self-start lg:overflow-auto">
        {error && <Alert>{error}</Alert>}
        {loadError && <Alert>{loadError}</Alert>}
        {/* One consolidated "Your marks" card — a study-marks section, a
            look-ups section, and a highlights section, each shown only when it
            has items (reclaims the vertical space the three separate cards ate). */}
        <div className="card space-y-3 p-4">
          <h3 className="text-sm font-semibold text-ink-soft">{t("Your marks")}</h3>

          {deep.length === 0 && sidebarLookups.length === 0 && highlights.length === 0 && (
            <p className="text-xs text-ink-faint">
              {t("Drag a box → “Mark to study” to add sections to learn + be tested on.")}
            </p>
          )}

          {deep.length > 0 && (
            <div>
              <span className="text-[11px] font-semibold uppercase text-ink-faint">
                {t("To master ({{count}})", { count: deep.length })}
              </span>
              <ul className="mt-1 space-y-2">
                {deep.map((d) => (
                  <li key={d.id} className="rounded-lg bg-black/5 p-2">
                    <input
                      value={d.label}
                      onChange={(e) => setLabel(d.id, e.target.value)}
                      className="w-full bg-transparent text-sm font-medium focus:outline-none"
                    />
                    <div className="mt-1 flex items-center gap-1">
                      {DEPTHS.map((dp) => (
                        <button
                          key={dp}
                          onClick={() => setMarkLevel(d.id, dp)}
                          className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                            d.depth === dp
                              ? "bg-brand-600 text-white"
                              : "bg-white text-ink-faint hover:bg-brand-50"
                          }`}
                        >
                          {t(DEPTH_LABELS[dp])}
                        </button>
                      ))}
                      <button
                        onClick={() => removeDeep(d.id)}
                        className="ml-auto text-xs text-red-500 hover:underline"
                      >
                        {t("Remove")}
                      </button>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {DIRECTIVE_PRESETS.map((p) => {
                        const on = (d.directives ?? []).includes(p.key);
                        return (
                          <button
                            key={p.key}
                            onClick={() => toggleDirective(d.id, p.key)}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              on ? "bg-brand-600 text-white" : "bg-white text-ink-faint hover:bg-brand-50"
                            }`}
                          >
                            {t(p.label)}
                          </button>
                        );
                      })}
                      {(d.directives ?? [])
                        .filter((x) => !DIRECTIVE_PRESETS.some((p) => p.key === x))
                        .map((x) => (
                          <button
                            key={x}
                            onClick={() => toggleDirective(d.id, x)}
                            title={t("Remove")}
                            className="rounded-full bg-brand-100 px-2 py-0.5 text-[10px] text-brand-700"
                          >
                            {x} ✕
                          </button>
                        ))}
                      <button
                        onClick={() => {
                          const input = window.prompt(t("Add a custom instruction for this section"));
                          if (input) addCustomDirective(d.id, input);
                        }}
                        className="rounded-full border border-dashed border-black/20 px-2 py-0.5 text-[10px] text-ink-faint hover:bg-black/5"
                      >
                        {t("+ custom")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sidebarLookups.length > 0 && (
            <div className="border-t border-black/5 pt-3">
              <span className="text-[11px] font-semibold uppercase text-ink-faint">
                {t("Lookups kept ({{count}})", { count: sidebarLookups.length })}
              </span>
              <ul className="mt-1 space-y-1">
                {sidebarLookups.map((l) => {
                const label = l.question ?? l.kind.replace(/_/g, " ");
                if (l.status === "loading") {
                  return (
                    <li key={l.id} className="flex items-center gap-2 px-1 py-0.5 text-xs text-ink-faint">
                      <Spinner className="h-3.5 w-3.5" />
                      <span className="truncate">{label}</span>
                    </li>
                  );
                }
                if (l.status === "error") {
                  return (
                    <li key={l.id} className="flex items-center gap-1 px-1 py-0.5 text-xs">
                      <span className="flex-1 truncate text-red-500">
                        {label} — {l.errorMsg || t("Lookup failed — tap Retry.")}
                      </span>
                      <button
                        onClick={() => retryLookup(l.id)}
                        className="shrink-0 rounded px-1 py-0.5 font-semibold text-red-500 hover:bg-red-50"
                      >
                        {t("Retry")}
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={l.id}>
                    <button
                      onClick={() => openDetail(l)}
                      className="flex w-full items-baseline gap-1 truncate rounded px-1 py-0.5 text-left text-xs text-ink-faint hover:bg-brand-50"
                    >
                      <span className="shrink-0">{label} —</span>
                      <span className="truncate">{previewText(l.explanation)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

          {highlights.length > 0 && (
            <div className="border-t border-black/5 pt-3">
              <span className="text-[11px] font-semibold uppercase text-ink-faint">
                {t("Highlights ({{count}})", { count: highlights.length })}
              </span>
              <p className="mt-0.5 text-[11px] text-ink-faint">
                {t("Visual only — not sent to the AI. “Mark to study” to include one in a set.")}
              </p>
              <ul className="mt-1 space-y-1">
                {highlights.map((h) => (
                  <li key={h.id} className="flex items-center gap-1">
                    <span
                      className="h-3 w-3 shrink-0 rounded-sm"
                      style={{ background: h.color ?? HIGHLIGHT_COLOR }}
                    />
                    <button
                      onClick={() => setSelectedMarkId(h.id)}
                      className="flex-1 truncate rounded px-1 py-0.5 text-left text-xs text-ink-faint hover:bg-brand-50"
                    >
                      {h.selectedText?.trim() || `${t("Highlight")} · p.${h.page}`}
                    </button>
                    <button
                      onClick={() => setMarkLevel(h.id, "recap_briefly")}
                      className="shrink-0 rounded px-1 py-0.5 text-[11px] font-semibold text-brand-600 hover:bg-brand-50"
                    >
                      {t("Mark to study")}
                    </button>
                    <button
                      onClick={() => removeHighlight(h.id)}
                      className="shrink-0 text-[11px] text-red-500 hover:underline"
                    >
                      {t("Remove")}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {primaryAction && (
          <Button
            size="lg"
            className="w-full"
            onClick={firePrimary}
            loading={primaryAction.loading}
            disabled={primaryAction.loading || (deep.length === 0 && keptLookups.length === 0)}
          >
            {primaryAction.label}
          </Button>
        )}
        {footerNote && (
          <p className="text-center text-[11px] text-ink-faint">
            {savedAt ? t("Saved ✓") + " " : ""}
            {footerNote}
          </p>
        )}
      </div>

      {/* Ask modal — fires a background lookup and closes immediately (both modes). */}
      {ask && (
        <AskModal
          onCancel={() => setAskFor(null)}
          onSubmit={(q) => {
            fireLookupFromPending(ask, "question", q);
            setAskFor(null);
          }}
        />
      )}

      {/* Detail modal */}
      {det && (
        <DetailModal
          key={det.id}
          mark={det}
          onClose={() => setDetail(null)}
          onRemove={() => removeLookup(det.id)}
          onEdit={(text) => setLookupText(det.id, text)}
          onToggleKeep={() => {
            const next = lookups.map((l) => (l.id === det.id ? { ...l, keep: !l.keep } : l));
            setLookups(next);
            persist(deep, next);
            setDetail({ ...det, keep: !det.keep });
          }}
        />
      )}

      {/* Undo snackbar — a removed mark can be restored (also Cmd/Ctrl+Z). */}
      {undoToast && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex justify-center px-4">
          <div className="pointer-events-auto flex items-center gap-3 rounded-full bg-gray-900 px-4 py-2 text-sm text-white shadow-soft">
            <span className="truncate">{undoToast}</span>
            <button
              onClick={undoRemove}
              className="shrink-0 font-semibold text-brand-300 hover:text-brand-200"
            >
              {t("Undo")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AskModal({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (q: string) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-soft">
        <h3 className="text-lg font-bold">{t("Ask about this")}</h3>
        <textarea
          value={q}
          onChange={(e) => setQ(e.target.value)}
          rows={3}
          placeholder={t("Type your question…")}
          className="mt-3 w-full rounded-xl border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            {t("Cancel")}
          </Button>
          <Button onClick={() => onSubmit(q)} disabled={!q.trim()}>
            {t("Ask")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// One labelled section of a specialist deep card (Definition / Explanation /
// Intuition / Check yourself), body rendered with markdown + math.
function FacetSection({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-600">
        {label}
      </span>
      <RichText className="mt-0.5" text={body} />
    </div>
  );
}

function DetailModal({
  mark,
  onClose,
  onRemove,
  onToggleKeep,
  onEdit,
}: {
  mark: LookupLocal;
  onClose: () => void;
  onRemove: () => void;
  onToggleKeep: () => void;
  onEdit: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(mark.explanation);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-5 shadow-soft">
        {/* A plot lookup shows the rendered chart prominently (full width); other
            lookups show the source snip as a small thumbnail. */}
        {mark.imageUrl && (
          <img
            src={mark.imageUrl}
            alt={t("plot")}
            className="mb-3 max-h-72 w-full rounded-lg border border-black/10 object-contain"
          />
        )}
        <div className="flex items-start gap-3">
          <div className="min-w-0">
            <span className="text-[11px] font-semibold uppercase text-brand-600">
              {mark.kind.replace(/_/g, " ")}
            </span>
            {mark.question && <p className="font-semibold">{mark.question}</p>}
          </div>
          <button
            type="button"
            onClick={() => {
              if (editing) onEdit(draft.trim());
              setEditing((v) => !v);
            }}
            className="ml-auto shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-brand-600 hover:bg-brand-50"
          >
            {editing ? t("Save") : t("Edit")}
          </button>
        </div>
        {editing ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={8}
            className="mt-3 w-full rounded-xl border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        ) : mark.definition || mark.intuition || mark.answer ? (
          // Specialist deep card → labelled facet sections (the `answer`/`definition`/
          // `intuition` fields are card-only; a plain /explain or Ask lookup has none).
          <div className="mt-3 space-y-3 text-sm text-ink">
            {mark.definition && <FacetSection label={t("Definition")} body={mark.definition} />}
            {mark.explanation && <FacetSection label={t("Explanation")} body={mark.explanation} />}
            {mark.intuition && <FacetSection label={t("Intuition")} body={mark.intuition} />}
            {mark.question && (
              <FacetSection
                label={t("Check yourself")}
                body={mark.question + (mark.answer ? `\n\n${mark.answer}` : "")}
              />
            )}
          </div>
        ) : (
          <div className="mt-3 text-sm text-ink">
            <RichText text={mark.explanation} />
          </div>
        )}
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-ink-soft">
            <input type="checkbox" checked={mark.keep} onChange={onToggleKeep} />
            {t("Keep in my set")}
          </label>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onRemove}>
              {t("Remove")}
            </Button>
            <Button onClick={onClose}>{t("Done")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
